import React, { useCallback } from 'react';
import { Spinner } from '@heroui/react';
import { Typography, UniversalTable } from '../../../../components';
import { EmptyContentClientIc } from '../../../../components/icons';
import { useTranslations } from '../../../../contexts/translation.context';
import { SubscribersTableProps, SubscriberData } from '../types';
import { TABLE_COLUMNS, getTableColumns } from '../constants';
import SubscriberIdCell from './SubscriberIdCell';
import SubscriberCampaignCell from './SubscriberCampaignCell';
import SubscriberUserCell from './SubscriberUserCell';
import SubscriberDateCell from './SubscriberDateCell';

const SubscribersTable: React.FC<SubscribersTableProps> = ({
  subscribers,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  sortBy,
  onSort,
}) => {
  const { t } = useTranslations();
  const renderCell = useCallback((subscriber: SubscriberData, columnKey: string) => {
    switch (columnKey) {
      case 'id':
        return <SubscriberIdCell subscriber={subscriber} />;
      case 'campaign':
        return <SubscriberCampaignCell subscriber={subscriber} />;
      case 'user':
        return <SubscriberUserCell subscriber={subscriber} />;
      case 'createdAt':
        return <SubscriberDateCell subscriber={subscriber} />;
      default:
        return null;
    }
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <UniversalTable
      mobileSize="md"
      data={subscribers}
      columns={getTableColumns(t)}
      renderCell={renderCell}
      emptyContent={
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <EmptyContentClientIc />
            <div className="flex flex-col gap-1.5">
              <Typography variant="h6" className="text-xl font-semibold leading-7">
                {t('subscribers.table.empty.title')}
              </Typography>
              <Typography
                className="text-neutral-500 text-base font-medium leading-6"
                variant="body1"
              >
                {t('subscribers.table.empty.description')}
              </Typography>
            </div>
          </div>
        </div>
      }
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
      sortBy={sortBy}
      onSort={onSort}
    />
  );
};

export default SubscribersTable;
